import React from 'react';
import { Modal, Button, TextField, IconButton } from '@mui/material';
import { Close, Edit, Delete } from '@mui/icons-material';
import { Zone, YardSpot } from '../../pages/YardSpotterApp/yardDataFetch';

interface ZoneManagementModalProps {
  open: boolean;
  zones: Zone[];
  yardSpots: YardSpot[];
  newZoneName: string;
  newZoneColor: string;
  onClose: () => void;
  onShowZoneEdit: (zone: Zone) => void;
  onDeleteZone: (zoneId: string) => void;
  onAddNewZone: () => void;
  onNewZoneNameChange: (name: string) => void;
  onNewZoneColorChange: (color: string) => void;
}

const ZoneManagementModal: React.FC<ZoneManagementModalProps> = ({
  open,
  zones,
  yardSpots,
  newZoneName,
  newZoneColor,
  onClose,
  onShowZoneEdit,
  onDeleteZone,
  onAddNewZone,
  onNewZoneNameChange,
  onNewZoneColorChange,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[500px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={onClose}
          />
          <h1 className="text-xl font-bold mb-5">Manage Yard Zones</h1>
          
          <div className="mb-5">
            <h2 className="text-lg font-semibold mb-3">Current Zones</h2>
            <div className="space-y-2">
              {zones.map(zone => (
                <div 
                  key={zone.id} 
                  className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ backgroundColor: zone.color }}
                    ></div>
                    <span className="font-medium">{zone.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {yardSpots.filter(spot => spot.zoneId === zone.id).length} spots
                    </span>
                    <IconButton size="small" onClick={() => onShowZoneEdit(zone)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => onDeleteZone(zone.id)}
                      disabled={yardSpots.filter(spot => spot.zoneId === zone.id).length > 0}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mb-5 border-t pt-5">
            <h2 className="text-lg font-semibold mb-3">Add New Zone</h2>
            <div className="flex gap-3 mb-3">
              <TextField
                label="Zone Name"
                variant="outlined"
                size="small"
                fullWidth
                value={newZoneName}
                onChange={(e) => onNewZoneNameChange(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newZoneColor}
                  onChange={(e) => onNewZoneColorChange(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer"
                />
              </div>
            </div>
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              onClick={onAddNewZone}
              disabled={!newZoneName.trim()}
            >
              Add Zone
            </Button>
          </div>
          
          <div className="flex justify-end pt-3 border-t">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ZoneManagementModal;

